import { atom, WritableAtom } from 'jotai';

export interface PendingReaction {
  queueId: string;
  emoji: string;
  targetMessageId: string;
  timestamp: string;
  conversationId: string;
  conversationType: 'dm' | 'group';
}

// Map keyed by targetMessageId - one pending reaction per message
export const pendingReactionsAtom = atom<Map<string, PendingReaction>>(
  new Map(),
);

export const addPendingReactionAtom: WritableAtom<
  null,
  [
    {
      targetMessageId: string;
      queueId: string;
      emoji: string;
      conversationId: string;
      conversationType: 'dm' | 'group';
    },
  ],
  void
> = atom(
  null,
  (
    get,
    set,
    {
      targetMessageId,
      queueId,
      emoji,
      conversationId,
      conversationType,
    }: {
      targetMessageId: string;
      queueId: string;
      emoji: string;
      conversationId: string;
      conversationType: 'dm' | 'group';
    },
  ) => {
    const currentMap = new Map(get(pendingReactionsAtom));

    currentMap.set(targetMessageId, {
      queueId,
      emoji,
      targetMessageId,
      timestamp: new Date().toISOString(),
      conversationId,
      conversationType,
    });

    set(pendingReactionsAtom, currentMap);
  },
);

export const removePendingReactionByQueueIdAtom: WritableAtom<
  null,
  [string],
  void
> = atom(null, (get, set, queueId: string) => {
  const currentMap = new Map(get(pendingReactionsAtom));

  for (const [targetMessageId, reaction] of currentMap.entries()) {
    if (reaction.queueId === queueId) {
      currentMap.delete(targetMessageId);
      break;
    }
  }

  set(pendingReactionsAtom, currentMap);
});

export const syncPendingReactionsAtom: WritableAtom<
  null,
  [PendingReaction[]],
  void
> = atom(null, (_get, set, reactions: PendingReaction[]) => {
  const newMap = new Map<string, PendingReaction>();
  reactions.forEach((reaction) => {
    newMap.set(reaction.targetMessageId, reaction);
  });

  set(pendingReactionsAtom, newMap);
});

export const removePendingReactionByTargetAtom: WritableAtom<
  null,
  [string],
  void
> = atom(null, (get, set, targetMessageId: string) => {
  const currentMap = new Map(get(pendingReactionsAtom));
  if (currentMap.has(targetMessageId)) {
    currentMap.delete(targetMessageId);
  }
  set(pendingReactionsAtom, currentMap);
});

export const clearPendingReactionsAtom: WritableAtom<null, [], void> = atom(
  null,
  (_get, set) => {
    set(pendingReactionsAtom, new Map());
  },
);
